import React, { useEffect, useMemo, useState } from 'react';

import {
	SortDirection,
	useDeleteSongMutation,
	useMergeSongsMutation,
	useReanalyzeSongsMutation,
	useSearchSongsMutation,
} from '@/graphql';
import {
	Button,
	ButtonGroup,
	Checkbox,
	Flex,
	FormControl,
	FormLabel,
	Input,
	Popover,
	PopoverArrow,
	PopoverBody,
	PopoverContent,
	PopoverHeader,
	PopoverTrigger,
	Spinner,
	Text,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';

import CreateSongModal, {
	CreateSongInitialValues,
} from './components/CreateSongModal';
import SongRow from './components/SongRow';

const PER_PAGE = 10;

const Songs = () => {
	const {
		data,
		error,
		isLoading,
		mutateAsync: searchSongs,
	} = useSearchSongsMutation({
		mutationKey: ['searchSongs'],
	});
	const [page, setPage] = useState(1);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const toast = useToast();
	const [initialData, setInitialData] = useState<CreateSongInitialValues>();
	const [searchParam, setSearchParam] = useState('');
	const [withLyrics, setWithLyrics] = useState(false);
	const [onlySelected, setOnlySelected] = useState(false);
	const [viewAs, setViewAs] = useState<'grid' | 'rows'>('rows');
	const [selectedSongs, setSelectedSongs] = useState<string[]>([]);
	const [sortBy, setSortBy] = useState<'name' | 'teamCount'>('teamCount');
	const [sortDirection, setSortDirection] = useState<SortDirection>(
		SortDirection.Desc,
	);

	const searchInput = useMemo(
		() => ({
			input: {
				limit: PER_PAGE,
				offset: page * PER_PAGE,
				sortBy,
				direction: sortDirection,
			},
			filters: {
				term: searchParam,
				withLyrics: withLyrics || false,
			},
		}),
		[page, searchParam, withLyrics, sortBy, sortDirection],
	);

	useEffect(() => {
		searchSongs(searchInput);
	}, [searchInput, searchSongs]);

	const { mutateAsync } = useDeleteSongMutation({
		onSuccess: () => {
			searchSongs(searchInput);
		},
	});
	const { mutateAsync: mergeSongs, isLoading: isMergeLoading } =
		useMergeSongsMutation({
			onSuccess: () => {
				searchSongs(searchInput);
			},
		});

	const { mutateAsync: reanalyzeSongs, isLoading: isReanalyzeLoading } =
		useReanalyzeSongsMutation({
			onSuccess: () => {
				searchSongs(searchInput);
			},
		});

	const mergeSelectedSongs = async () => {
		await mergeSongs({
			songIds: selectedSongs.map((id) => id),
		});
		setSelectedSongs([]);
	};

	if (isLoading) {
		return <Spinner />;
	}

	if (error) {
		throw error;
	}

	return (
		<>
			<CreateSongModal
				isOpen={isOpen}
				onClose={onClose}
				onSuccess={() => {
					searchSongs(searchInput);
				}}
				initialData={initialData}
			/>
			<Flex justifyContent="center" gap="10px">
				<Button
					display="block"
					my="50px"
					flexShrink={0}
					isLoading={isReanalyzeLoading}
					onClick={async () => {
						const reanalyzeSongsRes = await reanalyzeSongs({});
						toast({
							title: `${reanalyzeSongsRes.reanalyzeSongs} songs reanalyzed successfully!`,
							status: 'success',
						});
					}}
				>
					Reanalyze songs
				</Button>
				<Button
					display="block"
					my="50px"
					flexShrink={0}
					onClick={() => {
						setInitialData(undefined);
						onOpen();
					}}
				>
					Create song
				</Button>
			</Flex>
			<Flex
				gap={5}
				sx={{ position: 'sticky', top: '0' }}
				bg="white"
				zIndex={100}
				p={5}
			>
				<FormControl flexBasis={1} flexGrow={1} flexShrink={1}>
					<FormLabel>Search param</FormLabel>
					<Input
						placeholder={
							withLyrics ? 'Artist, Name, Lyrics' : 'Artist, Name'
						}
						type={'text'}
						onChange={(e) => {
							setSearchParam(e.currentTarget.value);
							setPage(0);
						}}
						value={searchParam}
					/>
				</FormControl>

				<FormControl flexGrow={0} flexShrink={1} flexBasis="100px">
					<FormLabel>With Lyrics</FormLabel>
					<Checkbox
						size="lg"
						onChange={(e) => setWithLyrics(e.target.checked)}
						isChecked={withLyrics}
					/>
				</FormControl>

				<FormControl flexGrow={0} flexShrink={1} flexBasis="120px">
					<FormLabel>Only Selected</FormLabel>
					<Checkbox
						size="lg"
						onChange={(e) => setOnlySelected(e.target.checked)}
						isChecked={onlySelected}
					/>
				</FormControl>

				<FormControl flexGrow={0} flexShrink={1} flexBasis="160px">
					<FormLabel>Sort by</FormLabel>
					<ButtonGroup size="sm" isAttached>
						<Button
							colorScheme={
								sortBy === 'teamCount' ? 'purple' : 'gray'
							}
							onClick={() => {
								if (sortBy === 'teamCount') {
									setSortDirection((d) =>
										d === SortDirection.Desc
											? SortDirection.Asc
											: SortDirection.Desc,
									);
								} else {
									setSortBy('teamCount');
									setSortDirection(SortDirection.Desc);
								}
								setPage(0);
							}}
						>
							Teams{' '}
							{sortBy === 'teamCount'
								? sortDirection === SortDirection.Desc
									? '↓'
									: '↑'
								: ''}
						</Button>
						<Button
							colorScheme={sortBy === 'name' ? 'blue' : 'gray'}
							onClick={() => {
								if (sortBy === 'name') {
									setSortDirection((d) =>
										d === SortDirection.Asc
											? SortDirection.Desc
											: SortDirection.Asc,
									);
								} else {
									setSortBy('name');
									setSortDirection(SortDirection.Asc);
								}
								setPage(0);
							}}
						>
							Name{' '}
							{sortBy === 'name'
								? sortDirection === SortDirection.Asc
									? '↑'
									: '↓'
								: ''}
						</Button>
					</ButtonGroup>
				</FormControl>

				<FormControl flexGrow={0} flexShrink={1} flexBasis="100px">
					<FormLabel>Actions</FormLabel>
					<ButtonGroup>
						<Button
							isDisabled={selectedSongs.length < 2}
							onClick={() => mergeSelectedSongs()}
							isLoading={isMergeLoading}
						>
							Merge ({selectedSongs.length})
						</Button>
						<Button
							isDisabled={!selectedSongs.length}
							onClick={() => setSelectedSongs([])}
						>
							Clear
						</Button>
					</ButtonGroup>
				</FormControl>
			</Flex>
			<Text>
				Showing songs {page * PER_PAGE} to {(page + 1) * PER_PAGE} of{' '}
				{data?.searchSongs.totalCount}
			</Text>
			<Flex gap={2} mt="30px" alignItems="stretch" flexWrap="wrap">
				{data?.searchSongs.items.map((song) => {
					if (!song) {
						return null;
					}

					const selectOrder =
						selectedSongs.findIndex((sid) => sid === song.id) + 1;

					if (onlySelected && selectOrder < 1) {
						return null;
					}

					return (
						<SongRow
							key={song.id}
							overlay={selectOrder}
							overlayColor={
								selectOrder === 1 ? 'green.200' : undefined
							}
							id={song.id}
							songThemes={{
								emotionTags: song.emotionTags,
								energyTags: song.energyTags,
								gospelTags: song.gospelTags,
								songTypeTags: song.songTypeTags,
							}}
							rotationStatus={song.rotationStatus}
							name={song.name}
							artworkUrl={song.artworkUrl}
							artist={song.artist}
							variations={JSON.stringify(song.variations)}
							analysisOutput={song.analysisOutput}
							teamCount={song.teamCount}
							onRotationStatusChange={() => {
								searchSongs(searchInput);
							}}
							onReanalyzeSuccess={(data) => {
								toast({
									title: `Song ${song.name} reanalyzed!`,
									description: `${data.reanalyzeSong.analysisOutput?.emotions.length} emotions, ${data.reanalyzeSong.analysisOutput?.thematicKeywords?.length} thematic keywords, ${data.reanalyzeSong.analysisOutput?.theologicalKeywords?.length} theological keywords, ${data.reanalyzeSong.analysisOutput?.gospelStory?.length} gospel story`,
									status: 'success',
									duration: 5000,
								});
							}}
							footer={
								<ButtonGroup spacing="3">
									<Button
										size="sm"
										variant="outline"
										onClick={() => {
											setInitialData({
												...song,
												analysisOutput:
													song.analysisOutput
														? JSON.stringify(
																song.analysisOutput,
																null,
																2,
														  )
														: '',
												variations: JSON.stringify(
													song.variations,
												),
											});
											onOpen();
										}}
									>
										Edit
									</Button>
									<Popover>
										{({ onClose }) => (
											<>
												<PopoverTrigger>
													<Button
														variant="ghost"
														colorScheme="red"
														size="sm"
													>
														Delete
													</Button>
												</PopoverTrigger>
												<PopoverContent>
													<PopoverArrow />
													<PopoverHeader>
														Confirmation!
													</PopoverHeader>
													<PopoverBody>
														<ButtonGroup
															spacing="2"
															w="100%"
														>
															<Button
																variant="solid"
																colorScheme="red"
																onClick={async () => {
																	try {
																		await mutateAsync(
																			{
																				songId: song.id,
																			},
																		);

																		toast({
																			title: `Song ${song.name} deleted!`,
																		});
																		onClose();
																	} catch (err) {
																		console.error(
																			err,
																		);
																		toast({
																			title: 'Error deleting song!',
																			status: 'error',
																			description:
																				err instanceof
																				Error
																					? err.message
																					: 'Unkown error, check console',
																		});
																	}
																}}
															>
																Yes
															</Button>
															<Button
																variant="ghost"
																onClick={
																	onClose
																}
															>
																No
															</Button>
														</ButtonGroup>
													</PopoverBody>
												</PopoverContent>
											</>
										)}
									</Popover>
									<Checkbox
										isChecked={selectedSongs.includes(
											song.id,
										)}
										onChange={(e) => {
											if (e.target.checked) {
												setSelectedSongs([
													...selectedSongs,
													song.id,
												]);
											} else {
												setSelectedSongs(
													selectedSongs.filter(
														(id) => id !== song.id,
													),
												);
											}
										}}
									/>
								</ButtonGroup>
							}
						/>
					);
				})}
			</Flex>

			<Text mt="20px">
				Showing songs {page * PER_PAGE} to {(page + 1) * PER_PAGE} of{' '}
				{data?.searchSongs.totalCount}
			</Text>
			<Flex justifyContent="center" gap="10px" mb="50px">
				<Button
					isDisabled={page === 0}
					mt="20px"
					onClick={() => {
						setPage(page - 1);
					}}
				>
					Previous Page
				</Button>

				<Button
					isDisabled={
						data && data.searchSongs.items.length < PER_PAGE
					}
					mt="20px"
					onClick={() => {
						setPage(page + 1);
					}}
				>
					Next Page
				</Button>
			</Flex>
		</>
	);
};

export default Songs;
